import VueRouter, { RawLocation } from 'vue-router';
import type { IRouteLink } from '@gig/magic-common';
import { RoutablePage } from '@gig/magic-common';
import { RouteName } from '../../routes/routes.constants';
import {
    WalletViewRouteName,
    WalletHistoryViewRouteName,
} from '@gig/magic-wallet';
import { isIncludedRoute } from './helpers/routes';
import { getModals } from '../../util';
import {
    detectRouteNameFromUrl,
    getLobbyPath,
    getPathFromLobbyMap,
} from './helpers/game.helpers';
import { GAMES_CATEGORIES_LIST } from '@gig/magic-messages';
import { ICategory } from '@gig/magic-sdk/dist/adapter/games/category';
import { Language } from '@gig/magic-sdk/dist/adapter/users/language';

function createLink(
    router: VueRouter,
    location: RawLocation,
    preserveQuery = false,
    preserveHash = false,
) {
    const { currentRoute } = router;

    const targetLocation = router.resolve(location, currentRoute).location;

    if (preserveQuery) {
        targetLocation.query = {
            ...targetLocation.query,
            ...currentRoute.query,
        };
    }

    if (preserveHash) {
        targetLocation.hash = window.location.hash || undefined;
    }

    const resolvedLocation = router.resolve(targetLocation, currentRoute);

    return {
        href: resolvedLocation.href,
        replace() {
            return router.replace(resolvedLocation.href).catch(() => null);
        },
        go() {
            return router.push(resolvedLocation.href).catch(() => null);
        },
        isActive() {
            const current = router.app.$route;
            const resolved = resolvedLocation.resolved;

            if (!current) {
                return false;
            }

            if (isIncludedRoute(current, resolved)) {
                return true;
            }

            const allModals = getModals(current.query, router);
            return allModals.some((route) => {
                return isIncludedRoute(route, resolved);
            });
        },
    };
}

/**
 * Routes to the specified location, route event handler for
 * magic components.
 * @export
 * @param {VueRouter} router
 * @param {IRouteLink} link
 * @returns
 */
export function getRoute(router: VueRouter, link: IRouteLink) {
    if (
        link.params?.language === Language.NORWEGIAN &&
        window.location.pathname.includes('/nw')
    ) {
        link.params.language = Language.NW_NORWEGIAN;
    }
    switch (link.route) {
        case RoutablePage.HOME:
            return createLink(router, '/');
        case RoutablePage.PASS_RESET_REQUEST:
            return createLink(router, { name: RouteName.PASSWORD_RESET });
        case RoutablePage.PASS_RESET_EMAIL:
            return createLink(router, { name: RouteName.PASSWORD_RESET_EMAIL });
        case RoutablePage.PASS_RESET_SMS:
            return createLink(router, { name: RouteName.PASSWORD_RESET_SMS });
        case RoutablePage.PASS_RESET_SECURITY_QUESTION:
            return createLink(router, {
                name: RouteName.PASSWORD_RESET_QUESTION,
            });
        case RoutablePage.PASS_RESET_CONFIRMATION:
            return createLink(router, {
                name: RouteName.PASSWORD_RESET_CONFIRMATION,
            });
        case RoutablePage.PASS_RESET_CONFIRMATION_DONE:
            return createLink(router, '/');
        case RoutablePage.PASS_RESET_SUCCESS:
            return createLink(router, {
                name: RouteName.PASSWORD_RESET_SUCCESS,
            });
        case RoutablePage.SIGN_UP:
            return createLink(router, {
                name: RouteName.SIGN_UP,
                params: link.params,
            });
        case RoutablePage.SIGN_UP_BANKID:
            return createLink(router, {
                name: RouteName.SIGN_UP_BANKID,
                params: link.params,
            });
        case RoutablePage.SIGN_UP_HYBRID:
            return createLink(router, {
                name: RouteName.SIGN_UP_HYBRID,
            });
        case RoutablePage.SIGN_UP_DONE:
            return createLink(router, '/');
        case RoutablePage.LOG_IN:
            return createLink(router, { name: RouteName.LOGIN });
        case RoutablePage.LOG_IN_DONE:
            // for different post-login actions in login forms located
            // in different places, consider adding a link param
            // and checking it here to decide what to do
            const to = (link.params && link.params.backTo) || {
                name: RouteName.HOME,
            };
            return createLink(router, to, true);
        case RoutablePage.ACCOUNT:
            return createLink(router, { name: RouteName.ACCOUNT });
        case RoutablePage.PROFILE:
            return createLink(router, { name: RouteName.PROFILE });
        case RoutablePage.CHANGE_EMAIL:
            return createLink(router, { name: RouteName.CHANGE_EMAIL });
        case RoutablePage.CHANGE_PASSWORD:
            return createLink(router, { name: RouteName.CHANGE_PASSWORD });
        case RoutablePage.CHANGE_SECURITY_QUESTION:
            return createLink(router, {
                name: RouteName.CHANGE_SECURITY_QUESTION,
            });
        case RoutablePage.MARKETING_PREFERENCES:
            return createLink(router, {
                name: RouteName.MARKETING_PREFERENCES,
            });
        case RoutablePage.ACCOUNT_CLOSURE:
            return createLink(router, { name: RouteName.ACCOUNT_CLOSURE });
        case RoutablePage.ACCOUNT_CLOSURE_FORM:
            return createLink(router, { name: RouteName.ACCOUNT_CLOSURE_FORM });
        case RoutablePage.ACCOUNT_CLOSURE_SUCCESS:
            return createLink(router, {
                name: RouteName.ACCOUNT_CLOSURE_SUCCESS,
            });
        case RoutablePage.ACCOUNT_CLOSURE_ERROR:
            return createLink(router, {
                name: RouteName.ACCOUNT_CLOSURE_ERROR,
                params: link.params,
            });
        case RoutablePage.ACCOUNT_CLOSURE_DONE:
            return createLink(router, { name: RouteName.HOME });
        case RoutablePage.RESPONSIBLE_GAMING:
            return createLink(router, {
                name: RouteName.RESPONSIBLE_GAMING,
                hash: link.hash,
                params: link.params,
            });
        case RoutablePage.VIEW_LIMITS:
            return createLink(router, { name: RouteName.VIEW_LIMITS });
        case RoutablePage.CREATE_LIMITS:
            return createLink(router, { name: RouteName.CREATE_LIMITS });
        case RoutablePage.SELF_EXCLUDE:
            return createLink(router, {
                name: RouteName.SELF_EXCLUDE,
                params: link.params,
            });
        case RoutablePage.PANIC_SELF_EXCLUDE:
            return createLink(router, {
                name: RouteName.PANIC_SELF_EXCLUDE,
                params: link.params,
            });
        case RoutablePage.TIME_OUT:
            return createLink(router, { name: RouteName.TIME_OUT });
        case RoutablePage.PLAY_CONTROL:
            return createLink(router, { name: RouteName.PLAY_CONTROL });
        case RoutablePage.MESSAGES:
            return createLink(router, { name: RouteName.MESSAGES });
        case RoutablePage.MESSAGE:
            return createLink(router, {
                name: RouteName.MESSAGES,
                params: link.params,
            });
        case RoutablePage.VERIFICATION:
            return createLink(router, { name: RouteName.VERIFICATION });
        case RoutablePage.SOW:
            return createLink(router, { name: RouteName.SOW });
        case RoutablePage.USER_INFO:
            return createLink(router, { name: RouteName.USER_INFO });
        case RoutablePage.BINGO_LOBBY:
            return createLink(router, {
                name: RouteName.BINGO_LOBBY,
                params: link.params,
            });
        case RoutablePage.LIVE_CHAT:
            return createLink(router, {
                name: RouteName.LIVE_CHAT,
                params: {
                    ...link?.params,
                },
            });
        case RoutablePage.WALLET_BONUS:
            return createLink(router, {
                name: RouteName.WALLET,
                params: { view: WalletViewRouteName.BONUSES },
            });
        case RoutablePage.WALLET_DEPOSIT: {
            const params = {
                view: WalletViewRouteName.DEPOSIT,
                ...link?.params,
            };
            return createLink(router, {
                name: RouteName.WALLET,
                params,
                query: link.query,
            });
        }
        case RoutablePage.WALLET_HISTORY:
            return createLink(router, {
                name: RouteName.TRANSACTION_HISTORY,
            });
        case RoutablePage.WALLET_HISTORY_GAMING: {
            const params = {
                view: WalletViewRouteName.HISTORY,
                type: WalletHistoryViewRouteName.GAMING,
                ...link?.params,
            };
            return createLink(router, {
                name: RouteName.TRANSACTION_HISTORY,
                params,
                query: link.query,
            });
        }
        case RoutablePage.WALLET_WITHDRAW: {
            const params = {
                view: WalletViewRouteName.WITHDRAW,
                ...link?.params,
            };
            return createLink(router, {
                name: RouteName.WALLET,
                params,
            });
        }
        case RoutablePage.REWARD_DETAIL:
            return createLink(router, {
                name: RouteName.REWARD,
                params: link.params,
            });
        case RoutablePage.REWARDS:
            return createLink(router, {
                name: RouteName.REWARDS,
            });
        case RoutablePage.OFFER:
            return createLink(router, {
                name: RouteName.OFFER,
                params: {
                    slug: link.params && link.params.slug,
                },
            });
        case RoutablePage.PROMOTIONS:
            return createLink(router, {
                name: RouteName.PROMOTIONS,
            });
        case RoutablePage.PROMOTION:
            return createLink(router, {
                name: RouteName.PROMOTION,
                params: {
                    slug: link.params && link.params.slug,
                },
            });
        case RoutablePage.GAMES:
            const name = detectRouteNameFromUrl();
            return createLink(router, {
                name,
                params: {
                    categorySlug:
                        (link.params && link.params.categorySlug) || undefined,
                },
            });
        case RoutablePage.LIVE_CASINO:
            return createLink(router, {
                name: RouteName.LIVE_CASINO,
                params: {
                    categorySlug: link.params?.categorySlug || undefined,
                },
            });
        case RoutablePage.GAMEPLAY:
            const lobby = getLobby(
                link.params?.categorySlug,
                link.meta.lobbies,
            );

            return createLink(router, {
                name: RouteName.GAMEPLAY,
                params: {
                    ...link.params,
                    lobby,
                },
            });
        case RoutablePage.SEARCH:
            return createLink(router, {
                name: RouteName.SEARCH,
            });
        case RoutablePage.GAMEPLAY_DONE:
            const toLobby = link.params?.lobby || {
                name: RouteName.GAMES,
            };
            return createLink(router, { name: toLobby });
        case RoutablePage.FAQ:
            return createLink(router, { name: RouteName.FAQ_CATEGORIES });
        case RoutablePage.FAQ_CATEGORY:
            return createLink(router, {
                name: RouteName.FAQ_ENTRIES,
                params: {
                    categorySlug: link.params && link.params.categorySlug,
                },
            });
        case RoutablePage.REALITY_CHECK:
            return createLink(router, {
                name: RouteName.REALITY_CHECK,
            });
        case RoutablePage.RESPONSIBLE_GAMING_PAGE:
            return createLink(router, {
                name: RouteName.RESPONSIBLE_GAMING_PAGE,
                hash: link.hash,
                params: {
                    categorySlug: link.params && link.params.categorySlug,
                },
            });
        case RoutablePage.SPORTSBOOK:
            return createLink(router, {
                name: RouteName.SPORTSBOOK,
            });
        case RoutablePage.SPORTSBOOK_INTERNAL:
            return createLink(
                router,
                {
                    name: RouteName.SPORTSBOOK,
                    params: {
                        sports: link.params?.sports,
                    },
                },
                true,
                true,
            );
        case RoutablePage.LEADERBOARDS:
            return createLink(router, {
                name: RouteName.LEADERBOARDS,
                params: {
                    page: link.params?.page ?? undefined,
                },
            });
        case RoutablePage.TOURNAMENTS:
            return createLink(router, {
                name: RouteName.TOURNAMENTS,
            });
        case RoutablePage.TOURNAMENTS_STARTING_SOON:
            return createLink(router, {
                name: RouteName.TOURNAMENTS_STARTING_SOON,
            });
        case RoutablePage.TOURNAMENTS_HISTORY:
            return createLink(router, {
                name: RouteName.TOURNAMENTS_HISTORY,
            });
        case RoutablePage.ACCOUNT_CUSTOM_ITEM:
            return createLink(router, {
                name: RouteName.ACCOUNT_CUSTOM_ITEM,
            });
        case RoutablePage.TWO_FACTOR_AUTHENTICATION:
            return createLink(router, {
                name: RouteName.TWO_FACTOR_AUTHENTICATION,
            });
        case RoutablePage.LOTBA_OLD_SELF_EXCLUSION_LINK:
            return createLink(router, {
                name: RouteName.LOTBA_OLD_SELF_EXCLUSION_LINK,
            });
        case RoutablePage.LANDING:
            return createLink(router, {
                name: RouteName.LANDING,
                params: link.params,
            });
        case RoutablePage.LANDING_SIGNUP:
            return createLink(router, {
                name: RouteName.LANDING_SIGNUP,
                params: link.params,
            });
        case RoutablePage.FREE_TO_PLAY:
            return createLink(router, {
                name: RouteName.FREE_TO_PLAY,
            });
        case RoutablePage.PROMOTION_PLAY:
            return createLink(router, {
                name: RouteName.PROMOTION_PLAY,
                params: link.params,
            });
        case RoutablePage.SET_LIMITS:
            return createLink(router, { name: RouteName.SET_LIMITS });
        case RoutablePage.BINGO_LOBBY:
            return createLink(router, {
                name: RouteName.BINGO_LOBBY,
                params: link.params,
            });
        case RoutablePage.PERSONALISED_OFFER:
            return createLink(router, {
                name: RouteName.PERSONALISED_OFFER,
                params: link.params,
            });
        case RoutablePage.PROVIDERS:
            return createLink(router, {
                name: RouteName.PROVIDERS,
                params: {
                    slug: link?.params?.slug || '',
                },
            });
        default:
            break;
    }
    return createLink(router, '/404');
}

function getLobby(categorySlug: string | undefined, lobbies: string[]): string {
    const category = getCategory(categorySlug);
    if (!categorySlug || !category) {
        return getLobbyPath(lobbies);
    }

    return getPathFromLobbyMap(category.lobby);
}

function getCategory(categorySlug: string | undefined): ICategory | undefined {
    const categories: ICategory[] =
        window.eventBus.getLatest(GAMES_CATEGORIES_LIST) || [];
    return categories.find((c) => c.slug === categorySlug);
}
